import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/utils';
import type { TimeLineSegment } from '../../columbusTypes';
import { COLUMBUS_COLORS } from '../../columbusColors';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';
import { Slider } from './Slider';
import type { SegmentRenderProps } from './Slider';

interface Props {
  dateFrom: string | null;
  dateTo: string | null;
  segments: TimeLineSegment[];
  onAddSegment: (next: number) => void;
  onRemoveSegment: (next: number) => void;
  onChangeColor: (next: number, colorCbId: number | null) => void;
  onSetAllSegments: (newSegments: TimeLineSegment[]) => void;
}

function getMaxDays(dateFrom: string | null, dateTo: string | null): number | null {
  if (!dateFrom || !dateTo) return null;
  const diff = Math.ceil((new Date(dateTo).getTime() - new Date(dateFrom).getTime()) / 86400000);
  return diff > 0 ? diff : null;
}

function dayOffsetToLabel(dateFrom: string, offset: number): string {
  const d = new Date(dateFrom);
  d.setDate(d.getDate() + offset);
  return d.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit' });
}

function ColorDot({ bgClass }: { bgClass: string }) {
  return (
    <span
      className={cn(
        'inline-block h-2.5 w-2.5 flex-none rounded-full border border-black/10',
        bgClass
      )}
    />
  );
}

function ColorSelect({
  colorCbId,
  onChange,
}: {
  colorCbId: number | null;
  onChange: (v: number | null) => void;
}) {
  const { t } = useTranslation('columbus-planner');
  const selectedColor = COLUMBUS_COLORS.find((c) => c.id === colorCbId);

  return (
    <div className="w-fit">
      <Select
        value={colorCbId != null ? String(colorCbId) : '__none__'}
        onValueChange={(v) => onChange(v === '__none__' ? null : Number(v))}
      >
        <SelectTrigger className="h-7 gap-1.5 px-2 text-xs">
          <SelectValue>
            {selectedColor ? (
              <span className="flex items-center gap-1.5">
                <ColorDot bgClass={selectedColor.bgClass} />
                {t(selectedColor.i18nKey)}
              </span>
            ) : (
              <span className="text-muted-foreground">{t('colorSelect.noColor')}</span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">
            <span className="text-muted-foreground">{t('colorSelect.noColor')}</span>
          </SelectItem>
          {COLUMBUS_COLORS.map((c) => (
            <SelectItem key={c.id} value={String(c.id)}>
              <span className="flex items-center gap-1.5">
                <ColorDot bgClass={c.bgClass} />
                {t(c.i18nKey)}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function TimeLineSlider({
  dateFrom,
  dateTo,
  segments,
  onAddSegment,
  onRemoveSegment,
  onChangeColor,
  onSetAllSegments,
}: Props) {
  const maxDays = getMaxDays(dateFrom, dateTo);

  // Rozděl segmenty na handles (pozice < maxDays) a terminální (pozice === maxDays)
  const handleSegments = maxDays != null ? segments.filter((s) => s.next < maxDays) : segments;
  const terminalSegment = maxDays != null ? segments.find((s) => s.next === maxDays) : undefined;

  const sliderValues = handleSegments.map((s) => s.next);

  // Barvy: jedna na každý viditelný pás (handles.length + 1)
  const sliderColors: (string | undefined)[] = [
    ...handleSegments.map((s) => COLUMBUS_COLORS.find((c) => c.id === s.colorCbId)?.hex),
    COLUMBUS_COLORS.find((c) => c.id === terminalSegment?.colorCbId)?.hex,
  ];

  const getLabel = (days: number): string => {
    if (!dateFrom) return `+${days}d`;
    return dayOffsetToLabel(dateFrom, days);
  };

  const handleSliderChange = (change: number[] | number) => {
    if (typeof change === 'number') {
      // Klik – přidat nebo odebrat handle (nikdy terminální segment)
      const exists = handleSegments.some((s) => s.next === change);
      if (exists) onRemoveSegment(change);
      else onAddSegment(change);
    } else {
      // Drag – přemísti handles, zachovej barvy a terminální segment
      const newHandleSegs: TimeLineSegment[] = change.map((next, i) => ({
        next,
        colorCbId: handleSegments[i]?.colorCbId ?? null,
      }));
      onSetAllSegments([...newHandleSegs, ...(terminalSegment ? [terminalSegment] : [])]);
    }
  };

  const handleLastBandColor = (newColor: number | null) => {
    if (maxDays == null) return;
    if (newColor === null) {
      // Bez barvy – terminální segment odeber
      if (terminalSegment) {
        onSetAllSegments(segments.filter((s) => s.next !== maxDays));
      }
    } else if (terminalSegment) {
      onChangeColor(maxDays, newColor);
    } else {
      // Přidej terminální segment s barvou najednou
      onSetAllSegments([...handleSegments, { next: maxDays, colorCbId: newColor }]);
    }
  };

  const renderSegment = ({ segmentIndex }: SegmentRenderProps) => {
    const isLastBand = segmentIndex === handleSegments.length;

    if (isLastBand) {
      return (
        <ColorSelect
          colorCbId={terminalSegment?.colorCbId ?? null}
          onChange={handleLastBandColor}
        />
      );
    }

    const seg = handleSegments[segmentIndex];
    if (!seg) return null;
    return (
      <ColorSelect
        colorCbId={seg.colorCbId}
        onChange={(newColor) => onChangeColor(seg.next, newColor)}
      />
    );
  };

  if (maxDays === null) return null;

  return (
    <Slider
      values={sliderValues}
      onChange={handleSliderChange}
      maxValue={maxDays}
      colors={sliderColors}
      getLabel={getLabel}
      renderSegment={renderSegment}
    />
  );
}
